import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { User, Alert } from '../types';

// ==================== TYPES ====================

interface AppState {
  user: User | null;
  isAuthenticated: boolean;
  alerts: Alert[];
}

type AppAction =
  | { type: 'LOGIN'; payload: User }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_ALERTS'; payload: Alert[] }
  | { type: 'MARK_ALERT_READ'; payload: number }
  | { type: 'MARK_ALL_ALERTS_READ' }
  | { type: 'DELETE_ALERT'; payload: number };

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  
  // Helper functions
  login: (mobile: string, pin: string) => boolean;
  logout: () => void;
  getUnreadAlertsCount: () => number;
  markAlertAsRead: (alertId: number) => void;
  markAllAlertsAsRead: () => void;
  deleteAlert: (alertId: number) => void;
}

// ==================== INITIAL STATE ====================

const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  alerts: [], // Empty array - will be fetched from API if needed
};

// ==================== REDUCER ====================

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
      };

    case 'UPDATE_ALERTS':
      return {
        ...state,
        alerts: action.payload,
      };

    case 'MARK_ALERT_READ':
      return {
        ...state,
        alerts: state.alerts.map(alert =>
          alert.id === action.payload
            ? { ...alert, isRed: true }
            : alert
        ),
      };

    case 'MARK_ALL_ALERTS_READ':
      return {
        ...state,
        alerts: state.alerts.map(alert => ({ ...alert, isRed: true })),
      };

    case 'DELETE_ALERT':
      return {
        ...state,
        alerts: state.alerts.filter(alert => alert.id !== action.payload),
      };

    default:
      return state;
  }
};

// ==================== CONTEXT ====================

const AppContext = createContext<AppContextType | undefined>(undefined);

// ==================== PROVIDER ====================

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // ==================== AUTH FUNCTIONS ====================

  const login = (mobile: string, pin: string): boolean => {
    // Simple validation - in real app, this would be an API call
    if (mobile && pin.length === 4) {
      // Mock user for now - replace with actual API call
      const user: User = {
        id: '1',
        name: 'Admin User',
        username: 'admin',
        email: 'admin@example.com',
        phone: mobile,
        role: 'Admin',
        reportingTo: 'N/A',
        site: 'N/A',
      };
      dispatch({ type: 'LOGIN', payload: user });
      return true;
    }
    return false;
  };

  const logout = (): void => {
    dispatch({ type: 'LOGOUT' });
  };

  // ==================== ALERT FUNCTIONS ====================

  const getUnreadAlertsCount = (): number => {
    return state.alerts.filter(alert => !alert.isRed).length;
  };

  const markAlertAsRead = (alertId: number): void => {
    dispatch({ type: 'MARK_ALERT_READ', payload: alertId });
  };

  const markAllAlertsAsRead = (): void => {
    dispatch({ type: 'MARK_ALL_ALERTS_READ' });
  };

  const deleteAlert = (alertId: number): void => {
    dispatch({ type: 'DELETE_ALERT', payload: alertId });
  };

  // ==================== CONTEXT VALUE ====================

  const contextValue: AppContextType = {
    state,
    dispatch,
    login,
    logout,
    getUnreadAlertsCount,
    markAlertAsRead,
    markAllAlertsAsRead,
    deleteAlert,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// ==================== HOOK ====================

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};